import { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Spacer,
  Spinner,
  Icon,
  CheckBox,
  ITableProps,
  Tooltip,
  withErrorBoundary,
} from '@oliasoft-open-source/react-ui-library';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import { ConnectedProps, connect } from 'react-redux';
import { pick } from 'lodash';
import { HierarchyType } from 'src/enums/compare-estimates';
import { selectUserTemplates, selectCompanyTemplates } from 'src/store/entities/templates/selectors';
import type { ChangeEvent } from 'react';
import { formatTimeToUI } from 'src/views/time-tracker/utils/date/dateUtils';
import { ITemplate } from 'src/common/interfaces/templates.interfaces';
import {
  removeCompareItem,
  runConceptCompare,
  showCompareModalUpdated,
} from 'src/store/entities/compare-estimates/compare-estimates';
import { ICompare, ICompareActiveEstimate, IEstimateItem } from 'src/common/interfaces/compare-estimates.interfaces';
import type { TRootState } from 'src/store/store-types';
import { getHeaderLabels } from '../../campaigns/utils/utils';

interface ICompareEstimatesInputTableProps extends PropsFromRedux {
  activeCompare: ICompare;
  filteredEstimates: IEstimateItem[];
  activeEstimates: ICompareActiveEstimate[];
  setActiveTabIndex: (value: number) => void;
}
const CompareEstimatesInputTable = ({
  activeCompare,
  filteredEstimates,
  activeEstimates,
  setActiveTabIndex,
  templateOptions,
  removeCompareItem,
  runConceptCompare,
  showCompareModalUpdated,
}: ICompareEstimatesInputTableProps) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [selectedPage, setSelectedPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTemplates, setSelectedTemplates] = useState<{ datasetId: number; templateId: string }[]>([]);
  const [reuseSimulations, setReuseSimulations] = useState<string[]>(
    activeEstimates.filter((e) => e.simulation?.jobId && e.simulationResult).map((e) => e.projectId),
  );
  useEffect(() => {
    setFilters({});
    setSelectedPage(1);
    setRowsPerPage(10);
    setSelectedTemplates([]);
    setReuseSimulations(
      activeEstimates.filter((e) => e.simulation?.jobId && e.simulationResult).map((e) => e.projectId),
    );
  }, [activeCompare?.compareId]);
  const { designs: conceptDesigns = [] } = activeCompare;
  const handleOnClickConfirmSelection = () => {
    runConceptCompare({
      compare: activeCompare,
      reuseSimulations,
      templates: selectedTemplates,
    });
    setActiveTabIndex(1);
  };

  const hasConceptDesigns = conceptDesigns?.length > 0;

  const headerLabels = getHeaderLabels(t, translations, hasConceptDesigns);

  const headerRow = headerLabels?.map(({ label, identifier }) => {
    let width;

    if (label === t(translations.compare_automationMethod)) {
      width = '20%';
    } else if (identifier === HierarchyType.UseExisting) {
      width = '4%';
    } else {
      width = 'auto';
    }
    return {
      value: label,
      width,
    };
  });

  const headerFilter = headerLabels?.map((header) => {
    const { identifier } = header;
    const filterValue = filters[identifier] ?? '';
    return header.identifier === HierarchyType.AutomationMethod || header.identifier === HierarchyType.UseExisting
      ? {}
      : {
          key: identifier,
          value: filterValue,
          type: 'Input',
          placeholder: t(translations.search),
          onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
            setFilters((p) => ({
              ...p,
              [identifier]: ev.target.value,
            }));
          },
        };
  });

  const startIndex = (selectedPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const estimates = hasConceptDesigns ? conceptDesigns : filteredEstimates;
  const filteredData = estimates?.filter((design) =>
    Object.keys(filters).every(
      (key) =>
        filters[key] === '' ||
        design[key as keyof typeof design]?.toString()?.toLowerCase().includes(filters[key].toLowerCase()),
    ),
  );

  const rows = [
    ...filteredData?.map((estimate) => {
      const activeEstimate = activeEstimates.find((e) => e.projectId === estimate.projectId);
      const activeEstimateSimulation = activeEstimate?.simulation;
      let rowCells = Object.entries(
        pick(estimate, [
          hasConceptDesigns ? HierarchyType.EstimateName : HierarchyType.Name,
          HierarchyType.DesignName,
          HierarchyType.WellboreName,
          HierarchyType.WellName,
          HierarchyType.SiteName,
          HierarchyType.FieldName,
          HierarchyType.CountryName,
        ]),
      ).map((value) => ({
        value: value[1],
      }));

      if (conceptDesigns?.length > 0) {
        const additionalCell =
          estimate.projectId === null
            ? {
                options: templateOptions,
                value:
                  selectedTemplates.find((template) => template.datasetId === estimate.datasetId)?.templateId || null,
                type: 'Select',
                placeholder: t(translations.compare_selectRuleset),
                onChange: (ev: ChangeEvent<HTMLSelectElement>) => {
                  setSelectedTemplates((prev) => {
                    const index = prev.findIndex((template) => template.datasetId === estimate.datasetId);
                    if (index !== -1) {
                      prev[index].templateId = ev.target.value;
                      return [...prev];
                    } else {
                      return [
                        ...prev,
                        {
                          datasetId: estimate.datasetId!,
                          templateId: ev.target.value,
                        },
                      ];
                    }
                  });
                },
              }
            : {};
        const checkBox: any =
          activeEstimate?.simulationResult && (activeEstimateSimulation?.jobId || activeEstimate.jobId)
            ? {
                value: (
                  <Tooltip
                    text={
                      <>
                        {t(translations.campaigns_clickToUseExistingSimulation)}
                        <br />
                        {t(translations.campaigns_lastSimulation)}: {formatTimeToUI(activeEstimateSimulation?.created)}
                      </>
                    }
                  >
                    <CheckBox
                      checked={reuseSimulations.includes(estimate?.projectId || '')}
                      onChange={(e) =>
                        setReuseSimulations((prev) => {
                          if (e.target.checked && estimate.projectId) {
                            return [...prev, estimate.projectId];
                          } else {
                            return prev.filter((id) => id !== estimate.projectId);
                          }
                        })
                      }
                    />
                  </Tooltip>
                ),
              }
            : {};
        rowCells = [checkBox, additionalCell, ...rowCells];
      }
      return {
        cells: rowCells,
        actions: [
          {
            label: 'Delete',
            icon: 'minus',
            onClick: () =>
              removeCompareItem({
                compare: activeCompare,
                projectId: estimate.projectId!,
                datasetId: estimate.datasetId!,
              }),
          },
          !hasConceptDesigns && activeEstimates?.length > 0
            ? {
                icon:
                  (activeEstimateSimulation?.created || activeEstimate?.jobId) && activeEstimate?.simulationResult ? (
                    <Icon color="var(--color-background-success)" icon="success" />
                  ) : (
                    <Spinner colored tiny />
                  ),
              }
            : {
                icon:
                  (activeEstimateSimulation?.created || activeEstimate?.jobId) && activeEstimate?.simulationResult ? (
                    <Icon color="var(--color-background-success)" icon="success" />
                  ) : null,
              },
        ],
      };
    }),
  ];
  const filteredRows = rows.slice(startIndex, endIndex);

  const table: ITableProps['table'] = {
    fixedWidth: '100%',
    testId: 'comparisons-table-estimates',
    headers: [
      {
        cells: headerRow,
        actions: [
          hasConceptDesigns
            ? {
                primary: true,
                label: 'Add',
                icon: 'add',
                onClick: () => showCompareModalUpdated(true),
              }
            : null,
        ],
      },
      { cells: headerFilter },
    ],
    rows: filteredRows,
    footer: {
      pagination: {
        rowCount: rows?.length,
        selectedPage,
        rowsPerPage: {
          onChange: (evt) => setRowsPerPage(Number(evt.target.value)),
          options: [
            { label: `10 / ${t(translations.page)}`, value: 10 },
            { label: `20 / ${t(translations.page)}`, value: 20 },
            { label: `50 / ${t(translations.page)}`, value: 50 },
          ],
          value: rowsPerPage,
        },
        onSelectPage: setSelectedPage,
        small: true,
      },
    },
  };
  const hasNotRunInitialCalculation =
    conceptDesigns?.filter((design) => design.datasetId).length === selectedTemplates.length;

  return (
    <>
      <Table table={table} />
      {conceptDesigns?.length > 0 && (
        <>
          <Spacer />
          <Button
            label={t(translations.confirmSelection)}
            colored
            disabled={!hasNotRunInitialCalculation}
            onClick={handleOnClickConfirmSelection}
            testId="comparisons-button-confirm-selection"
          />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: TRootState) => {
  const userTemplates = selectUserTemplates(state);
  const companyTemplates = selectCompanyTemplates(state);

  const templateOptions = [
    {
      label: i18n.t(translations.userRulesets),
      type: 'Heading',
    },
    ...userTemplates.map((template: ITemplate) => ({
      label: template.highLevel.name,
      value: template.templateId,
    })),
    {
      label: i18n.t(translations.companyRulesets),
      type: 'Heading',
    },
    ...companyTemplates?.map((template: ITemplate) => ({
      label: template.highLevel.name,
      value: template.templateId,
    })),
  ];

  return {
    templateOptions,
  };
};

const mapDispatchToProps = {
  runConceptCompare,
  removeCompareItem,
  showCompareModalUpdated,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(CompareEstimatesInputTable));

export { Container as CompareEstimatesInputTable };
